/**
 * aw26-cart-upsell.js
 * Smart upsell carousel for the cart drawer.
 *
 * Sources products from a single configurable add-on collection (default:
 * accessories/bags) and orders them so the items that close the gap to the next
 * milestone come first: at a EUR 128 subtotal (EUR 22 from free shipping),
 * items >= EUR 22 lead, cheapest-that-clears first, then the rest by price
 * descending as the closest approximation.
 *
 * API discipline: one collection fetch, cached for 5 minutes with in-flight
 * dedup, run lazily the first time the drawer opens. It refreshes on cart:update
 * only while the drawer is actually visible. Config (thresholds, gift settings,
 * collection handle, i18n) is injected as JSON by
 * snippets/free-shipping-upsell-script.liquid so this file stays Liquid-free.
 */
(function () {
  if (window.__aw26UpsellBound) return;

  var cfgEl = document.querySelector('[data-aw26-upsell-config]');
  if (!cfgEl) return;

  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  window.__aw26UpsellBound = true;

  var COLLECTION = CFG.collection || 'accessoiries';
  var THRESHOLDS = CFG.thresholds || { DEFAULT: 0 };
  var GIFT_ENABLED = !!CFG.giftEnabled;
  var GIFT_THRESHOLD = CFG.giftThreshold || 0;
  var I18N = CFG.i18n || {};

  function getShippingThreshold() {
    var country = window.Shopify && window.Shopify.country ? window.Shopify.country : 'NL';
    return THRESHOLDS[country] || THRESHOLDS.DEFAULT || 0;
  }

  // Resolve the next unreached milestone. Returns { type, threshold, remaining }
  // or null when everything is reached.
  function getNextMilestone(cartTotal) {
    var shipping = getShippingThreshold();
    if (GIFT_ENABLED && GIFT_THRESHOLD > 0 && cartTotal < GIFT_THRESHOLD) {
      if (shipping === 0 || GIFT_THRESHOLD <= shipping) {
        return { type: 'gift', threshold: GIFT_THRESHOLD, remaining: GIFT_THRESHOLD - cartTotal };
      }
    }
    if (shipping > 0 && cartTotal < shipping) {
      return { type: 'shipping', threshold: shipping, remaining: shipping - cartTotal };
    }
    if (GIFT_ENABLED && GIFT_THRESHOLD > 0 && cartTotal < GIFT_THRESHOLD) {
      return { type: 'gift', threshold: GIFT_THRESHOLD, remaining: GIFT_THRESHOLD - cartTotal };
    }
    return null;
  }

  function getUpsellHeader(milestone) {
    if (!milestone) return I18N.header_default || '';
    if (milestone.type === 'gift') return I18N.header_gift || '';
    if (milestone.type === 'shipping') return I18N.header_shipping || '';
    return I18N.header_gift || '';
  }

  function setUpsellHeader(container, list, text) {
    var header = container.querySelector('.upsell-products__header');
    if (!header) {
      header = document.createElement('p');
      header.className = 'upsell-products__header';
      container.insertBefore(header, list);
    }
    if (header.textContent !== text) header.textContent = text;
  }

  // Single add-on collection fetch, cached (5 min TTL) + in-flight dedup.
  if (!window._aw26UpsellCollectionCache) {
    window._aw26UpsellCollectionCache = { data: null, time: 0, inflight: null, handle: null };
  }

  function getCollectionProducts() {
    var cache = window._aw26UpsellCollectionCache;
    var now = Date.now();
    if (cache.data && cache.handle === COLLECTION && now - cache.time < 300000) {
      return Promise.resolve(cache.data);
    }
    if (cache.inflight) return cache.inflight;
    cache.inflight = fetch('/collections/' + encodeURIComponent(COLLECTION) + '/products.json?limit=50')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        window._aw26UpsellCollectionCache = {
          data: data,
          time: Date.now(),
          inflight: null,
          handle: COLLECTION,
        };
        return data;
      })
      .catch(function (err) {
        window._aw26UpsellCollectionCache.inflight = null;
        throw err;
      });
    return cache.inflight;
  }

  function euro(value) {
    return '€' + value.toFixed(2).replace('.', ',');
  }

  function cardHtml(p) {
    var v = p._variant;
    var price = parseFloat(v.price);
    var compare = parseFloat(v.compare_at_price || 0);
    var onSale = compare > price;
    var multi = p.variants.length > 1;

    var img = p.images && p.images[0] ? p.images[0].src.replace(/\.(\w+)(\?.*)?$/, '_120x120.$1$2') : '';
    var title = p.title.length > 28 ? p.title.slice(0, 25) + '...' : p.title;

    var priceHtml = onSale
      ? '<span class="upsell-card__compare">' + euro(compare) + '</span><span class="upsell-card__price upsell-card__price--sale">' + euro(price) + '</span>'
      : '<span class="upsell-card__price">' + euro(price) + '</span>';

    var sizesHtml = '';
    if (multi) {
      sizesHtml = '<div class="upsell-card__sizes"><div class="upsell-card__sizes-inner">' +
        p.variants.slice(0, 10).map(function (vv) {
          return '<button type="button" class="upsell-card__size" data-upsell-add="' + vv.id + '"' + (vv.available ? '' : ' disabled') + '>' + (vv.option1 || vv.title) + '</button>';
        }).join('') +
        '</div></div>';
    }

    var btn = multi
      ? '<button type="button" class="upsell-card__add" data-upsell-expand>+</button>'
      : '<button type="button" class="upsell-card__add" data-upsell-add="' + v.id + '">+</button>';

    return '<div class="upsell-card' + (multi ? ' upsell-card--expandable' : '') + '">' +
      '<div class="upsell-card__top">' +
        '<div class="upsell-card__img">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div class="upsell-card__info">' +
          '<a href="/products/' + p.handle + '" class="upsell-card__title-link"><p class="upsell-card__title">' + title + '</p></a>' +
          '<div class="upsell-card__prices">' + priceHtml + '</div>' +
        '</div>' +
        btn +
      '</div>' +
      sizesHtml +
    '</div>';
  }

  function loadUpsell() {
    var container = document.querySelector('[data-upsell]');
    if (!container) return;
    var list = container.querySelector('[data-upsell-list]');
    if (!list) return;

    var cache = window._aw26UpsellCollectionCache;
    var hasFresh = cache.data && cache.handle === COLLECTION && Date.now() - cache.time < 300000;
    if (!hasFresh) {
      list.innerHTML = '<p style="font-size:12px;opacity:0.6;padding:5px;">' + (I18N.loading || '') + '</p>';
      container.style.display = 'block';
    }

    Promise.all([
      fetch('/cart.js').then(function (r) { return r.json(); }),
      getCollectionProducts(),
    ])
      .then(function (results) {
        var cart = results[0];
        var data = results[1];

        // Cart total, excluding items already claimed as a free gift, mirroring
        // the Liquid progress bar so the sizing matches the visible copy.
        var cartTotal = 0;
        var cartProductIds = [];
        (cart.items || []).forEach(function (item) {
          var isFreeGift = item.properties && item.properties._free_gift === 'true';
          if (item.requires_shipping && !isFreeGift) cartTotal += item.final_line_price;
          cartProductIds.push(String(item.product_id));
        });

        if (cart.item_count === 0) {
          container.style.display = 'none';
          return;
        }

        var milestone = getNextMilestone(cartTotal);
        container.style.display = 'block';
        setUpsellHeader(container, list, getUpsellHeader(milestone));

        var products = (data.products || []).filter(function (p) {
          if (cartProductIds.indexOf(String(p.id)) !== -1) return false;
          var t = (p.title || '').toLowerCase();
          if (t.indexOf('giftcard') !== -1 || t.indexOf('gift card') !== -1 || t.indexOf('cadeaukaart') !== -1) return false;
          var type = (p.product_type || '').toLowerCase();
          if (type === 'gift card' || type === 'giftcard') return false;
          // Free-gift products are published at price 0 (claimable only via the gift
          // selector). Never surface them as a paid upsell, even when they live in the
          // add-on collection. A real product is never priced 0 here.
          return p.variants && p.variants.some(function (v) { return v.available && parseFloat(v.price) > 0; });
        });

        products.forEach(function (p) {
          var av = p.variants.find(function (v) { return v.available; });
          p._price = av ? parseFloat(av.price) * 100 : 0;
          p._variant = av;
        });

        var ordered;
        if (milestone && milestone.remaining > 0) {
          // Items that clear the gap first (cheapest that clears leads), then
          // the rest by price descending (closest approximation to the gap).
          var remaining = milestone.remaining;
          var closers = products
            .filter(function (p) { return p._price >= remaining; })
            .sort(function (a, b) { return a._price - b._price; });
          var rest = products
            .filter(function (p) { return p._price < remaining; })
            .sort(function (a, b) { return b._price - a._price; });
          ordered = closers.concat(rest);
        } else {
          // Everything unlocked: show the curated add-on collection as-is.
          ordered = products;
        }

        var selected = ordered.slice(0, 5);
        if (selected.length === 0) {
          list.innerHTML = '';
          container.style.display = 'none';
          return;
        }

        list.innerHTML = selected.map(cardHtml).join('');
      })
      .catch(function () {
        container.style.display = 'none';
      });
  }

  // Click handlers (delegated, added once).
  if (!window._aw26UpsellClickBound) {
    window._aw26UpsellClickBound = true;
    document.addEventListener('click', function (e) {
      if (e.target.closest('.upsell-card__title-link')) return;

      var card = e.target.closest('.upsell-card--expandable');
      var expandBtn = e.target.closest('[data-upsell-expand]');
      if ((card || expandBtn) && !e.target.closest('[data-upsell-add]')) {
        e.preventDefault();
        var targetCard = expandBtn ? expandBtn.closest('.upsell-card') : card;
        var btn = targetCard.querySelector('[data-upsell-expand]');
        var sizes = targetCard.querySelector('.upsell-card__sizes');
        if (btn && sizes) {
          btn.classList.toggle('is-open');
          sizes.classList.toggle('is-visible');
        }
        return;
      }

      var addBtn = e.target.closest('[data-upsell-add]');
      if (!addBtn || addBtn.disabled) return;

      e.preventDefault();
      e.stopPropagation();

      var vid = addBtn.dataset.upsellAdd;
      var orig = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = '...';

      var cardEl = addBtn.closest('.upsell-card');
      var titleEl = cardEl ? cardEl.querySelector('.upsell-card__title') : null;
      var productName = titleEl ? titleEl.textContent.trim() : (I18N.unknown_product || '');
      var priceEl = cardEl ? cardEl.querySelector('.upsell-card__price') : null;
      var productPrice = priceEl ? priceEl.textContent.trim() : '';
      var variantName = addBtn.textContent.trim();
      if (variantName === '+') variantName = '';

      var comp = document.querySelector('cart-items-component');
      var sec = comp ? comp.dataset.sectionId : '';

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var existingNote = cart.note || '';
          var ts = new Date().toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
          var upsellLine = '[CART UPSELL] ' + productName + (variantName ? ' (' + variantName + ')' : '') + ' - ' + productPrice + ' - ' + ts;
          var newNote = existingNote ? existingNote + '\n' + upsellLine : upsellLine;

          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: parseInt(vid, 10),
                quantity: 1,
                properties: { '_upsell': 'true', '_upsell_source': 'cart_drawer', '_upsell_price': productPrice },
              }],
              sections: sec,
              sections_url: window.location.pathname,
            }),
          })
            .then(function (r) { return r.json(); })
            .then(function (state) {
              return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: newNote }),
              }).then(function () { return state; });
            });
        })
        .then(function (state) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: state, sourceId: 'upsell', data: { source: 'upsell', itemCount: 1, sections: state.sections || {} } },
          }));
          addBtn.textContent = '✓';
          setTimeout(function () {
            addBtn.textContent = orig;
            addBtn.disabled = false;
          }, 1200);
        })
        .catch(function () {
          addBtn.textContent = '!';
          setTimeout(function () { addBtn.textContent = orig; addBtn.disabled = false; }, 1200);
        });
    });
  }

  // Refresh on cart:update, but only when the drawer is actually open.
  if (!window._aw26UpsellCartListenerBound) {
    window._aw26UpsellCartListenerBound = true;
    document.addEventListener('cart:update', function () {
      var drawer = document.querySelector('dialog.cart-drawer__dialog[open], dialog.cart-drawer[open]');
      if (drawer) {
        setTimeout(loadUpsell, 300);
      } else {
        window._aw26UpsellLoadedForOpen = false;
      }
    });
  }

  // Bind to the drawer dialog directly (no global observer / polling).
  function bindDrawerOpenHandler() {
    if (window._aw26UpsellDrawerBound) return;
    var drawer = document.querySelector('dialog.cart-drawer__dialog, cart-drawer-component dialog, dialog.cart-drawer');
    if (!drawer) return;
    window._aw26UpsellDrawerBound = true;

    var observer = new MutationObserver(function () {
      if (drawer.hasAttribute('open') && !window._aw26UpsellLoadedForOpen) {
        window._aw26UpsellLoadedForOpen = true;
        loadUpsell();
      } else if (!drawer.hasAttribute('open')) {
        window._aw26UpsellLoadedForOpen = false;
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });

    if (drawer.hasAttribute('open')) {
      window._aw26UpsellLoadedForOpen = true;
      loadUpsell();
    }
  }

  bindDrawerOpenHandler();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindDrawerOpenHandler, { once: true });
  }
})();
